export const pitfallsModule = `# 通用陷阱集：Cloudflare + React 開發常見錯誤

> 版本：v1.0
> 更新原則：每次實際開發遇到問題並解決後，追加至此
> 用途：注入到每個 Layer Prompt 的「常見陷阱」章節

---

## 🔴 型別與資料一致性

### T1: 修改 CMS schema 時不同步三處
- **現象**：前台顯示正常但後台儲存後資料丟失，或 TypeScript 編譯報錯
- **根因**：\`apps/web/src/types/cms.ts\`、\`apps/api/src/types.ts\`、\`apps/api/src/data/default.ts\` 未同步更新
- **解決**：建立型別變更時的強制檢查清單
- **Prompt 防禦**：「修改任何 CMS schema 時，必須同時更新這三個檔案」

### T2: Deep Merge 覆蓋陣列
- **現象**：後台儲存「關於我們」區塊後，所有物業資料消失
- **根因**：\`{ ...old, ...new }\` 淺拷貝導致深層物件被覆蓋，或陣列被替換而非合併
- **解決**：使用遞迴 deep merge，陣列由 source 覆蓋 target
- **Prompt 防禦**：「POST /api/cms/data 必須使用 deep merge，絕不可使用淺拷貝」

---

## 🔴 部署與環境

### T3: 無 VITE_CMS_API_URL 時 build
- **現象**：\`/admin\` 登入報錯 \`SyntaxError: Unexpected end of JSON input\`
- **根因**：\`import.meta.env.VITE_CMS_API_URL\` 為空，API 呼叫變成相對路徑 \`/api/admin/login\`，Pages 靜態伺服器回傳 HTML 而非 JSON
- **解決**：build 前確認 \`.env\` 已填入正確 URL
- **Prompt 防禦**：「絕對不可在無 VITE_CMS_API_URL 的情況下 build」

### T4: 將密碼寫入 wrangler.toml
- **現象**：密碼洩露到 git history
- **根因**：\`ADMIN_PASSWORD\` 寫入 \`[vars]\` 而非 \`wrangler secret put\`
- **解決**：\`wrangler secret put ADMIN_PASSWORD\`
- **Prompt 防禦**：「生產環境 ADMIN_PASSWORD 必須用 wrangler secret put，不要寫死」

### T5: 忘記設定 SPA fallback
- **現象**：直接訪問 \`/properties/123\` 回傳 404
- **根因**：Cloudflare Pages 找不到 \`/properties/123.html\`
- **解決**：\`public/_redirects\` 加入 \`/* /index.html 200\`
- **Prompt 防禦**：「public/_redirects 必須存在且包含 SPA fallback 規則」

---

## 🔴 API 與 CORS

### T6: Worker 缺少 CORS headers
- **現象**：瀏覽器報錯 \`CORS policy: No 'Access-Control-Allow-Origin' header\`
- **根因**：Hono 的 cors middleware 未正確配置，或配置在其他路由之後
- **解決**：\`app.use('*', cors(...))\` 放在所有路由**之前**
- **Prompt 防禦**：「CORS middleware 必須是 Worker 的第一個中間件」

### T7: KV 讀寫缺少 await
- **現象**：資料更新後讀取仍是舊值，或出現 race condition
- **根因**：\`kv.get()\` 或 \`kv.put()\` 前沒加 \`await\`
- **解決**：所有 KV 操作強制使用 \`await\`
- **Prompt 防禦**：「所有 KV 操作前必須加 await」

---

## 🔴 前端細節

### T8: Footer 顯示後台連結
- **現象**：一般訪客可以看到 \`/admin\` 連結
- **根因**：Footer 導航未過濾後台路由
- **解決**：Footer 的 links 清單排除 \`/admin\`
- **Prompt 防禦**：「Footer 連結不要顯示 /admin 到一般訪客」

### T9: WhatsApp 號碼格式不一致
- **現象**：WhatsApp 連結無法開啟正確對話
- **根因**：儲存時帶 \`852\` 前綴，但連結時未去除非數字字符
- **解決**：儲存保留 \`852\`，連結時 \`number.replace(/\\D/g, '')\`
- **Prompt 防禦**：「WhatsApp 號碼儲存時保留 852 前綴，連結時去掉非數字」

### T10: Google Maps 連結亂碼
- **現象**：中文地址在 Google Maps 中顯示亂碼或無法定位
- **根因**：未使用 \`encodeURIComponent\` 編碼地址
- **解決**：\`https://maps.google.com/?q=\${encodeURIComponent(address)}\`
- **Prompt 防禦**：「Google Maps 連結使用 encodeURIComponent，避免亂碼」

### T11: Analytics 使用 localStorage
- **現象**：跨裝置訪客被計為同一 session
- **根因**：\`localStorage\` 持久化，session ID 不應跨裝置共享
- **解決**：使用 \`sessionStorage\`
- **Prompt 防禦**：「Analytics session 使用 sessionStorage 而非 localStorage」

---

## 🔴 媒體與圖片

### T12: KV 單筆超過 25MB
- **現象**：上傳大圖片時 Worker 報錯
- **根因**：Cloudflare KV 單筆 value 上限 25MB
- **解決**：啟用 R2，或壓縮圖片
- **Prompt 防禦**：「KV 單筆 25MB 上限，媒體總量軟限制 1GB；大量圖片請啟用 R2」

### T13: MediaPicker 單/多選混淆
- **現象**：封面圖選了多張，或相簿只能選一張
- **根因**：未區分 \`mode="single"\` 與 \`mode="multiple"\`
- **解決**：封面圖、Logo、人物照片用 single；物業相簿用 multiple
- **Prompt 防禦**：「MediaPicker 單/多選：mode=single 時只能選一張，mode=multiple 時可多選」

---

## 🔴 React 與狀態管理

### T14: Admin 未維護本地 draft state
- **現象**：後台修改後未點「儲存」就已影響前台顯示
- **根因**：直接修改全域 Context state
- **解決**：Admin 維護本地 \`draft\` state，點擊「儲存變更」才 \`POST /api/cms/data\`
- **Prompt 防禦**：「Admin 維護本地 draft state，點擊儲存才寫入 KV」

### T15: 物業篩選邏輯錯誤
- **現象**：選「出售」時還顯示「出租」的物業
- **根因**：\`sale_or_rent\` 類型的物業在篩選時被錯誤排除
- **解決**：售價篩選對 \`sale\` / \`sale_or_rent\` 生效；租金篩選對 \`rent\` / \`sale_or_rent\`（有 rentPrice 時）生效
- **Prompt 防禦**：「售價篩選只對 sale / sale_or_rent 生效；租金篩選只對 rent / sale_or_rent 生效」

---

## 🟡 提示詞編寫陷阱

### P1: Prompt 過長導致 AI 中間遺忘
- **現象**：生成的程式碼前半部分正確，後半部分偏離要求
- **根因**：AI context window 的「lost in the middle」效應
- **解決**：拆分為多個 Layer Prompt，每個控制在 200-300 行以內

### P2: 驗收標準寫成「給人看」的格式
- **現象**：AI 生成後不進行自我驗證
- **根因**：checkbox 列表 \`[ ]\` 對 AI 沒有約束力
- **解決**：改為「生成完成後的驗證步驟」編號列表

### P3: 部署設定與業務邏輯混在一起
- **現象**：修改業務邏輯時意外改到部署配置
- **根因**：單一 prompt 涵蓋所有層面
- **解決**：部署層獨立為 Layer 4
`;
